import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import LandingPage from '@/pages/LandingPage';
import Dashboard from '@/pages/Dashboard';
import Folio from '@/pages/FolioPage';
import ChildManager from '@/pages/ChildManager';
import AvatarBuilder from '@/pages/AvatarBuilder';
import StoryGenerator from '@/pages/StoryGenerator';
import StoryReader from '@/pages/StoryReader';
import StoryLibrary from '@/pages/StoryLibrary';
import SubscriptionPage from '@/pages/SubscriptionPage';
import ChildProfileDetail from '@/pages/ChildProfileDetail';
import SharedStoryViewer from '@/pages/SharedStoryViewer';
import PaymentSuccess from '@/pages/PaymentSuccess';
import CommunityPage from '@/pages/CommunityPage';
import AdminPanel from '@/pages/AdminPanel';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import InstallPrompt from '@/components/InstallPrompt';
import InstallButton from '@/components/InstallButton';
import NotificationManager from '@/components/NotificationManager';
import { Button } from '@/components/ui/button';
import { Moon, Sun, LogOut, Crown, Users, Star, House } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Public Story Viewer for non-logged-in users accessing shared story links
const PublicStoryViewer = () => {
  const { storyId } = useParams();
  const { nightMode } = useContext(AuthContext);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicStory = async () => {
      try {
        const response = await axios.get(`${API}/public/story/${storyId}`);
        setStory(response.data);
      } catch (err) {
        console.error('Failed to fetch public story:', err);
        setError('Story not found or not shared');
      } finally {
        setLoading(false);
      }
    };
    fetchPublicStory();
  }, [storyId]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${nightMode ? 'night-gradient' : 'playful-gradient'}`}>
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 animate-pulse" style={{ color: 'var(--accent-2)' }} />
          <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Loading story...
          </p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${nightMode ? 'night-gradient' : 'playful-gradient'}`}>
        <div className="story-card text-center py-12 max-w-md">
          <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Story Not Found
          </h1>
          <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
            {error || 'This story may have been removed or is no longer shared.'}
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="pill-button"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            Go to StoryTime Studio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-12 h-12" style={{ color: 'var(--accent-2)' }} strokeWidth={2.5} />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              {story.title}
            </h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Shared from StoryTime Studio • Reading Level: {story.reading_level}
          </p>
        </div>

        <div className="story-card p-8">
          {story.images && story.images.length > 0 && (
            <div className="mb-6 flex justify-center">
              <img
                src={`data:image/png;base64,${story.images[0]}`}
                alt="Story illustration"
                className="max-w-full h-auto rounded-2xl"
                style={{ maxHeight: '400px' }}
              />
            </div>
          )}
          
          <div className="prose max-w-none mb-8">
            <p className="text-lg leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>
              {story.content}
            </p>
          </div>
          
          {story.moral && (
            <div className="mt-8 p-6 rounded-2xl" style={{ background: 'var(--accent-5)', color: 'white' }}>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                The Moral
              </h3>
              <p className="text-lg">{story.moral}</p>
            </div>
          )}
        </div>

        <div className="mt-12 text-center story-card p-8">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Create your own stories!
          </h2>
          <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
            Generate personalized bedtime stories with children-friendly illustrations and teaching at <a href="https://dreamcanvasmaker.com" className="font-bold hover:underline">https://dreamcanvasmaker.com</a>
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="pill-button h-auto whitespace-normal"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            <BookOpen className="mr-2 w-5 h-5" />
            Try StoryTime Studio by Dream Canvas Maker 
          </Button>
        </div>
      </div>
    </div>
  );
};

// Global Header Component
const GlobalHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, nightMode, toggleNightMode, logout } = React.useContext(AuthContext);
  
  // Don't show header on landing page or shared story viewer
  if (location.pathname === '/' || location.pathname.startsWith('/shared/')) {
    return null;
  }
  
  // Don't show if no user
  if (!user) return null;
  
  const isGuest = user.id === 'guest-user';
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const handleUpgrade = () => {
    navigate('/subscription');
  };
  
  // Get page title based on route
  const getPageTitle = () => {
    if (location.pathname === '/dashboard') return 'StoryTime Studio';
    if (location.pathname === '/library') return 'Story Library';
    if (location.pathname === '/generate') return 'Story Generator';
    if (location.pathname === '/children') return 'Child Profiles';
    if (location.pathname === '/avatars') return 'Avatar Builder';
    if (location.pathname === '/subscription') return 'Subscription';
    if (location.pathname.startsWith('/story/')) return 'Story Reader';
    if (location.pathname.startsWith('/children/')) return 'Learning History';
    return 'StoryTime Studio';
  };
  
  return (
    <header 
      className="sticky top-0 z-50 py-3 px-4 sm:px-6"
      style={{ 
        background: 'var(--bg-secondary)', 
        borderBottom: '2px solid var(--sketch-border)' 
      }}
    >
      <div className="container mx-auto flex items-center justify-between gap-2">
        {/* Left side - App/Page Name */}
        <div 
          className="flex items-center gap-1 cursor-pointer"
          onClick={() => navigate('/dashboard')}
          title="Go to Dashboard"
        >
          <img 
            src="/icons/DCM-logo.png" 
            alt="by Dream Canvas Maker" 
            className={`w-6 h-6 sm:w-7 sm:h-7 object-contain rounded transition-all duration-200 ${
              nightMode ? 'invert brightness-0' : ''
            }`}
          />
          <House className="w-4 h-4" />
          <h1 className="text-sm sm:text-base font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            {getPageTitle()}
          </h1>
        </div>
        
        {/* Right side - Action Buttons */}
        <div className="flex items-center gap-0.5 sm:gap-1">
          {user?.role === 'admin' && (
            <Button
              onClick={() => navigate('/admin')}
              size="sm"
              className="rounded-full px-2 sm:px-3 h-8 sm:h-9 bg-yellow-200 text-yellow-800 hover:bg-yellow-300"
              title="Admin Panel"
            >
              <Star className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Admin</span>
            </Button>
          )}
          <InstallButton />
          <Button
            onClick={() => navigate('/community')}
            variant="outline"
            size="sm"
            className="rounded-full px-2 sm:px-3 h-8 sm:h-9"
            style={{
              background: nightMode ? 'var(--bg-secondary)' : 'white',
              color: nightMode ? 'var(--text-primary)' : 'inherit',
              borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
            }}
            title="Community Stories"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Community</span>
          </Button>
          <Button
            onClick={handleUpgrade}
            variant="outline"
            size="sm"
            className="rounded-full px-2 sm:px-3 h-8 sm:h-9"
            style={{
              background: nightMode ? 'var(--bg-secondary)' : 'white',
              color: nightMode ? 'var(--text-primary)' : 'inherit',
              borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
            }}
            title="Subscribe/Upgrade"
          >
            <Crown className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Upgrade</span>
          </Button>
          <NotificationManager />
          <Button
            onClick={toggleNightMode}
            variant="ghost"
            size="icon"
            className="rounded-full w-8 h-8 sm:w-9 sm:h-9"
            title={nightMode ? 'Switch to Day Mode' : 'Switch to Night Mode'}
          >
            {nightMode ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5" />}
          </Button>
          {!isGuest && (
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="rounded-full px-2 sm:px-3 h-8 sm:h-9"
              style={{
                background: nightMode ? 'var(--bg-secondary)' : 'white',
                color: nightMode ? 'var(--text-primary)' : 'inherit',
                borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
              }}
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

const GlobalFooter = () => {
  const location = useLocation();
  
  const allowedPaths = ['/', '/dashboard', '/privacy', '/terms', '/subscription'];
  if (!allowedPaths.includes(location.pathname)) return null;

  return (
    <footer className="py-6 text-center relative z-10">
      <div className="container mx-auto px-6">
        <div className="flex justify-center gap-6 mb-2">
          <a 
            href="/privacy" 
            className="text-sm hover:underline opacity-70 hover:opacity-100 transition-opacity"
            style={{ color: 'var(--text-secondary)' }}
          >
            Privacy Policy
          </a>
          <a 
            href="/terms" 
            className="text-sm hover:underline opacity-70 hover:opacity-100 transition-opacity"
            style={{ color: 'var(--text-secondary)' }}
          >
            Terms & Conditions
          </a>
          <span 
            className="text-sm opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          >
            v{process.env.REACT_APP_VERSION || window.APP_VERSION || '2.1.0'}
          </span>
        </div>
        <p className="text-xs opacity-50" style={{ color: 'var(--text-secondary)' }}>
          © {new Date().getFullYear()} StoryTime Studio
        </p>
      </div>
    </footer>
  );
};

function App() {
  const [user, setUser] = useState(null);
  // Migration: Clear old placeholder token
  const storedToken = localStorage.getItem('token');
  if (storedToken === 'session-token') {
    console.log('Migrating from old placeholder token...');
    localStorage.removeItem('token');
  }
  const [token, setToken] = useState(storedToken === 'session-token' ? null : storedToken);
  const [loading, setLoading] = useState(true);
  const [nightMode, setNightMode] = useState(false);
  const [processingSession, setProcessingSession] = useState(false);
  const hasFetchedUser = useRef(false);

  // Process Google OAuth session on mount
  useEffect(() => {
    // Force redirect from www to non-www to ensure consistent auth/cookies/recaptcha
    if (window.location.hostname === 'www.dreamcanvasmaker.com') {
      const newUrl = window.location.href.replace('www.dreamcanvasmaker.com', 'dreamcanvasmaker.com');
      window.location.replace(newUrl);
      return;
    }

    let hasProcessed = false;
    
    const processGoogleSession = async () => {
      // Check for OAuth errors in URL (from Emergent Auth redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
      
      // Check for error in both query params and hash
      const error = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
      
      if (error && !hasProcessed) {
        hasProcessed = true;
        console.error('OAuth error:', error, errorDescription);
        
        // Handle specific OAuth errors
        if (error === 'invalid_request' && errorDescription?.includes('state')) {
          toast.error('Sign-in session expired. Please try again.', {
            description: 'This can happen if you waited too long or had multiple login windows open.',
            duration: 6000,
            action: {
              label: 'Retry',
              onClick: () => {
                // Clear URL and redirect to landing page for fresh login
                window.location.href = '/';
              }
            }
          });
        } else {
          toast.error(`Authentication error: ${errorDescription || error}`, {
            duration: 5000,
            action: {
              label: 'Retry',
              onClick: () => window.location.href = '/'
            }
          });
        }
        
        // Clean URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        return;
      }
      
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (sessionId && !hasProcessed) {
        hasProcessed = true;
        setProcessingSession(true);
        try {
          // Debug: Log the reCAPTCHA key being used
          console.log('[reCAPTCHA] Using site key:', process.env.REACT_APP_RECAPTCHA_SITE_KEY);
          
          // Robustly ensure reCAPTCHA is loaded and ready
          const ensureRecaptchaLoaded = () => {
            return new Promise((resolve, reject) => {
              // 1. Check if already loaded
              if (window.grecaptcha && window.grecaptcha.ready) {
                return resolve();
              }

              // 2. Inject script if missing
              if (!document.querySelector(`script[src*="google.com/recaptcha/api.js"]`)) {
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
                console.log('[reCAPTCHA] Script injected');
              }

              // 3. Poll for global object
              let checks = 0;
              const interval = setInterval(() => {
                if (window.grecaptcha && window.grecaptcha.ready) {
                  clearInterval(interval);
                  resolve();
                }
                checks++;
                if (checks > 100) { // 10 seconds timeout
                  clearInterval(interval);
                  reject(new Error('reCAPTCHA global object load timeout'));
                }
              }, 100);
            });
          };

          await ensureRecaptchaLoaded();
          console.log('[reCAPTCHA] Global object ready');

          const recaptchaToken = await new Promise((resolve, reject) => {
            window.grecaptcha.ready(async () => {
              try {
                console.log('[reCAPTCHA] grecaptcha ready, executing...');
                const token = await window.grecaptcha.execute(
                  process.env.REACT_APP_RECAPTCHA_SITE_KEY,
                  { action: 'login' }
                );
                console.log('[reCAPTCHA] Token received:', token.substring(0, 20) + '...');
                resolve(token);
              } catch (err) {
                console.error('[reCAPTCHA] Execute error:', err);
                reject(err);
              }
            });
          });

          // Process session with backend
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId,
            recaptcha_token: recaptchaToken
          });

          // Set session token as cookie
          // Set cookie domain to root domain to share between www and non-www
          let cookieDomain = window.location.hostname;
          if (cookieDomain.endsWith('dreamcanvasmaker.com')) {
            cookieDomain = '.dreamcanvasmaker.com';
          }
          document.cookie = `session_token=${response.data.session_token}; domain=${cookieDomain}; path=/; secure; samesite=none; max-age=${7 * 24 * 60 * 60}`;

          // Update state - store actual session token as fallback for browsers that don't send cookies
          setUser(response.data.user);
          setToken(response.data.session_token);
          localStorage.setItem('token', response.data.session_token);

          // Clean URL - remove hash without triggering navigation
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, cleanUrl);

          toast.success('Welcome to StoryTime Studio!');
        } catch (error) {
          console.error('Session processing failed:', error);
          // Log detailed error info
          if (error.response) {
            console.error('Error response data:', error.response.data);
            console.error('Error response status:', error.response.status);
          }
          const errorMessage = error.response?.data?.detail || 'Authentication failed. Please try again.';
          toast.error(errorMessage, {
            action: {
              label: 'Retry',
              onClick: () => window.location.href = '/'
            }
          });
          // Clean URL on error
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, cleanUrl);
        } finally {
          setProcessingSession(false);
        }
      }
    };

    processGoogleSession();
  }, []);

  useEffect(() => {
    if (token && token !== 'guest-token' && !processingSession && !hasFetchedUser.current) {
      fetchUser();
    } else if (!processingSession) {
      setLoading(false);
    }
  }, [token, processingSession]);

  useEffect(() => {
    if (user?.night_mode !== undefined) {
      setNightMode(user.night_mode);
    }
  }, [user]);

  const fetchUser = async () => {
    if (hasFetchedUser.current) return; // Prevent multiple fetch attempts
    hasFetchedUser.current = true;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    hasFetchedUser.current = false; // Reset flag for new login
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
  };

  const logout = async () => {
    // Clear local state immediately to prevent race conditions
    hasFetchedUser.current = false; // Reset flag on logout
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    
    // Clear session cookie
    let cookieDomain = window.location.hostname;
    if (cookieDomain.endsWith('dreamcanvasmaker.com')) {
      cookieDomain = '.dreamcanvasmaker.com';
    }
    document.cookie = `session_token=; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    try {
      // Call backend logout to clear session
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleNightMode = async () => {
    const newMode = !nightMode;
    setNightMode(newMode);
    
    if (user && token !== 'guest-token') {
      try {
        await axios.patch(
          `${API}/auth/me`,
          { night_mode: newMode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUser({ ...user, night_mode: newMode });
      } catch (error) {
        console.error('Failed to update night mode:', error);
      }
    }
  };

  const refreshUser = useCallback(async () => {
    if (token && token !== 'guest-token') {
      try {
        const response = await axios.get(`${API}/auth/sync-and-get-me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
        return null;
      }
    }
    return null;
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100">
        <div className="text-2xl font-bold text-purple-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, token, login, logout, nightMode, toggleNightMode, refreshUser }}>
      <div className={nightMode ? 'night-mode' : 'day-mode'}>
        <BrowserRouter>
          <GlobalHeader />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/community/story/:storyId" element={<PublicStoryViewer />} />
            <Route path="/admin" element={user ? <AdminPanel /> : <Navigate to="/" />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/folio" element={user ? <Folio /> : <Navigate to="/" />} />
            <Route path="/children" element={user ? <ChildManager /> : <Navigate to="/" />} />
            <Route path="/avatars" element={user ? <AvatarBuilder /> : <Navigate to="/" />} />
            <Route path="/generate" element={user ? <StoryGenerator /> : <Navigate to="/" />} />
            <Route path="/library" element={user ? <StoryLibrary /> : <Navigate to="/" />} />
            <Route path="/story/:storyId" element={user ? <StoryReader /> : <PublicStoryViewer />} />
            {/* <Route path="/subscription" element={user ? <SubscriptionPage /> : <Navigate to="/" />} /> */}
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/" />} />
            <Route path="/children/:childId" element={user ? <ChildProfileDetail /> : <Navigate to="/" />} />
            <Route path="/shared/:shareKey" element={<SharedStoryViewer />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
          <GlobalFooter />
        </BrowserRouter>
        <Toaster richColors position="top-center" />
        <InstallPrompt />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
export { API };